import { ReportData, ReportExample } from "../types";
import { GROUNDING_KNOWLEDGE } from "../constants/grounding";

// Safely stringify objects that might contain circular references (like React events)
const _generateContent = async (prompt: string, model: string = 'gemini-4.5-flash') => {
  try {
    const userApiKey = localStorage.getItem('user_gemini_api_key') || '';
    const response = await fetch('/api/gemini/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': userApiKey
      },
      body: JSON.stringify({ prompt, model }),
    });

    if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error || `API Error: ${response.statusText}`);
    }

    const data = await response.json();
    return data.text;
  } catch (error: any) {
    console.error("Gemini API Error:", error?.message || error);
    throw error;
  }
};

export const generateSurgicalReport = async (data: ReportData, previousExamples?: ReportExample[]): Promise<string> => {
  const { date, attending, resident, secondResident, anesthesia, patient, findings, complications, implants, closure } = data;

  let learningContext = "";
  
  if (previousExamples && previousExamples.length > 0) {
      learningContext = `
      ---------------------------------------------------
      *** REFERENCE EXAMPLES (STYLE ALIGNMENT) ***
      Analyze the STYLE, TONE, and PHRASING of these approved reports:
      
      ${previousExamples.map((ex, i) => `
      --- EXAMPLE ${i+1} ---
      ${ex.reportText}
      `).join('\n')}
      ---------------------------------------------------
      `;
  }

  const prompt = `
    Role: Expert board-certified veterinary surgeon (DACVS).
    
    ${GROUNDING_KNOWLEDGE}

    ${learningContext}

    Current Patient Data:
    - Date: ${date}
    - Attending: ${attending}
    - Primary Resident: ${resident}
    - Secondary Resident: ${secondResident || 'None'}
    - Anesthesia: ${anesthesia}
    - Patient: ${patient.name} (${patient.species}, ${patient.weight}kg)
    - Procedure: ${patient.procedure}
    - Pre-Op Diagnosis: ${patient.preOpDiagnosis}
    - Post-Op Diagnosis: ${patient.postOpDiagnosis}
    - Findings: ${findings}
    - Implants: ${implants}
    - Closure Notes: ${closure}
    - Complications: ${complications}

    Strict Requirements:
    1. Header: You MUST start the report with a structured header exactly like this:
       DATE: ${date}
       ATTENDING SURGEON: ${attending}
       SURGICAL RESIDENT 1: ${resident}
       SURGICAL RESIDENT 2: ${secondResident || 'None'}
       ANESTHESIA SUPPORT: ${anesthesia}
       PROCEDURE: ${patient.procedure}
       ---------------------------------------------------

    Guidelines:
    1. Clinical Voice: Formal, objective, third-person passive voice.
    2. Precision: Use exact anatomical terminology.
    3. Terminology: Use specific brand names for suture (Monocryl, PDS, etc.).
    4. DACVS Standards: Fill in standard technical details for ${patient.procedure} expected in a professional medical record.

    Structural Requirements:
    1. Header (The mandatory section defined above)
    2. Approach (Narrative)
    3. Surgical Findings (Narrative based on input)
    4. Procedure (Technical step-by-step)
    5. Closure (Detailed layers and materials)
  `;

  try {
    const text = await _generateContent(prompt, 'gemini-3.5-flash');
    return text || "Error: No text generated.";
  } catch (error) {
    return "Error generating report. Please check your API key.";
  }
};

export const generatePostOpPlan = async (data: ReportData): Promise<string> => {
  const { patient, findings, complications } = data;

  const prompt = `
    Role: Expert Veterinary Surgeon (DACVS).
    Task: Generate a concise recovery plan for Inpatient Status Notes.
    
    ${GROUNDING_KNOWLEDGE}

    Case Context:
    - Procedure: ${patient.procedure}
    - Diagnosis: ${patient.postOpDiagnosis}
    - Surgical Findings: ${findings}
    - Complications: ${complications}

    Strict Rules:
    1. Monitoring: Specific needs based on findings (e.g., neuro checks, PCV/TS).
    2. Medications: 
       - FORMAT: "MedicationName 0.0 mg/kg frequency"
       - DO NOT categorize (no "Pain:", no "Abx:").
       - Prioritize Cefazolin/Unasyn for antibiotics.
    3. Exclusions: DO NOT list fluids. No conversational filler.
    
    Format: Bullet points.
  `;

  try {
    const text = await _generateContent(prompt, 'gemini-3.5-flash');
    return text || "Standard post-op monitoring.";
  } catch (error) {
    return "Standard post-op care.";
  }
};

export const generateSurgicalReference = async (query: string): Promise<string> => {
  const prompt = `
    You are an expert veterinary surgical resident advisor.
    The user wants a summary for: "${query}".
    
    ${GROUNDING_KNOWLEDGE}

    Provide a detailed summary based on the principles in Johnston & Tobias and the grounding data above.
    
    Format:
    ### Disease Process & Background
    ### Medical Management
    ### Surgical Management
    ### Post-operative Recovery & Prognosis
  `;

  try {
    const text = await _generateContent(prompt, 'gemini-3.5-flash');
    return text || "Error: No reference generated.";
  } catch (error) {
    return "Error generating reference.";
  }
};